import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import BaseButton from '../../components/BasicComponent/BaseButton'
import BaseInput from '../../components/BasicComponent/BaseInput'
import BasePopup from '../../components/BasicComponent/BasePopup'
import LoadingScreen from '../../components/BasicComponent/LoadingScreen'
import SafeImage from '../../components/BasicComponent/SafeImage'
import { VEHICLE_TYPE } from '../../constants/global'
import { PATH } from '../../constants/router'
import { OPTIONS_VEHICLE_TYPE_INFO_VEHICLE } from '../../constants/vehicle'
import { useTrafficFineVehicleContext } from '../../context/TrafficFineVehicleContext'
import { normalizePlate, sanitizePlateInput, validatePlateNumber } from '../../helper/validatorPlateNumber'
import useGetDetailOrderPayment from '../../hooks/payment/useGetDetailOrderPayment'
import useTrafficFinePayment, { shouldHandleTrafficFinePaymentAtVehicleStep } from '../../hooks/payment/useTrafficFinePayment'
import FixedBottom from '../../components/BasicComponent/FixedBottom'
import DraggableScroll from '../../components/BasicComponent/DraggableScroll'
import HeaderPartner from '../../components/BasicComponent/HeaderPartner'
import LayoutPartner from '../../components/BasicComponent/LayoutPartner'
import './index.scss'

function RemoveIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.4" />
      <path d="M7 7 13 13M13 7l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function getVehicleTypeInfo(vehicleType) {
  return OPTIONS_VEHICLE_TYPE_INFO_VEHICLE.find((item) => Number(item.vehicleType) === Number(vehicleType)) || OPTIONS_VEHICLE_TYPE_INFO_VEHICLE[0]
}

function getVehicleListTitle(packageVehicleType) {
  const normalizedPackageVehicleType = Number(packageVehicleType)

  if (normalizedPackageVehicleType === VEHICLE_TYPE.CAR) {
    return 'Danh sách ô tô'
  }

  // Gói family hiện là gói xe máy nhiều xe, nên title vẫn hiển thị theo nhóm xe máy.
  if ([VEHICLE_TYPE.MOTOBIKE, VEHICLE_TYPE.ELECTRIC_MOTORBIKE].includes(normalizedPackageVehicleType)) {
    return 'Danh sách xe máy'
  }

  return 'Danh sách xe'
}

function isRegistrationVehicleTypeCompatible(packageVehicleType, registrationVehicleType) {
  const normalizedPackageVehicleType = Number(packageVehicleType)
  const normalizedRegistrationVehicleType = Number(registrationVehicleType)

  if (!normalizedPackageVehicleType || !normalizedRegistrationVehicleType) {
    return false
  }

  if (normalizedPackageVehicleType === normalizedRegistrationVehicleType) {
    return true
  }

  if (
    normalizedPackageVehicleType === VEHICLE_TYPE.MOTOBIKE &&
    normalizedRegistrationVehicleType === VEHICLE_TYPE.ELECTRIC_MOTORBIKE
  ) {
    return true
  }

  return false
}

const VIRTUAL_KEYBOARD_HEIGHT_THRESHOLD = 120

function getViewportHeight() {
  if (typeof window === 'undefined') {
    return 0
  }

  return window.visualViewport?.height || window.innerHeight || 0
}

export default function TrafficFineNotificationVehicles() {
  const history = useHistory()
  const location = useLocation()
  const { listVehicleNotRegistered, listVehicleRegisteredPackage } = useTrafficFineVehicleContext()
  const locationState = location.state || {}
  const {
    orderId,
    isLoading: isLoadingOrderDetail,
    vehicles: orderVehicles,
    product: orderProduct,
    paymentMethodId: restoredPaymentMethodId,
    paymentMethodType: restoredPaymentMethodType
  } = useGetDetailOrderPayment()
  const shouldRestoreOrderDetail = !!orderId

  // Thong tin xe duoc truyen tu man truoc.
  const registrationVehicleState = locationState.trafficFineRegistrationVehicle || {}
  const locationRegistrationVehicle = {
    vehicleIdentity: registrationVehicleState?.vehicleIdentity || undefined,
    vehicleType: registrationVehicleState?.vehicleType ? Number(registrationVehicleState.vehicleType) : undefined
  }
  const orderRegistrationVehicle =
    orderVehicles.length > 0
      ? {
          vehicleIdentity: orderVehicles[0]?.plate || undefined,
          vehicleType: orderVehicles[0]?.vehicleType ? Number(orderVehicles[0].vehicleType) : undefined
        }
      : {
          vehicleIdentity: undefined,
          vehicleType: undefined
        }
  const trafficFineRegistrationVehicle = shouldRestoreOrderDetail ? orderRegistrationVehicle : locationRegistrationVehicle

  // Thong tin goi dich vu da chon.
  const locationRegistrationProduct = locationState.trafficFineRegistrationProduct || null
  const trafficFineRegistrationProduct = shouldRestoreOrderDetail ? orderProduct : locationRegistrationProduct
  const packageVehicleType = trafficFineRegistrationProduct?.vehicleType ? Number(trafficFineRegistrationProduct.vehicleType) : undefined
  const registrationVehicleType = trafficFineRegistrationVehicle?.vehicleType
  const registrationVehicleIdentity = trafficFineRegistrationVehicle?.vehicleIdentity || ''
  const isCompatibleRegistrationVehicleType = isRegistrationVehicleTypeCompatible(packageVehicleType, registrationVehicleType)

  // Chi uu tien vehicleType cua xe khi no phu hop voi vehicleType cua goi.
  const activeVehicleType = isCompatibleRegistrationVehicleType ? registrationVehicleType : packageVehicleType || registrationVehicleType
  const activeVehicleTypeInfo = activeVehicleType ? getVehicleTypeInfo(activeVehicleType) : null
  const maxVehicles = Math.max(Number(trafficFineRegistrationProduct?.productSize) || 1, 1)
  const vehicleListTitle = getVehicleListTitle(packageVehicleType)
  const defaultPlate = normalizePlate(registrationVehicleIdentity)
  const defaultVehicleFromState = useMemo(() => {
    if (!defaultPlate || !registrationVehicleType || !isCompatibleRegistrationVehicleType) {
      return null
    }

    return {
      plate: defaultPlate,
      vehicleType: registrationVehicleType
    }
  }, [defaultPlate, isCompatibleRegistrationVehicleType, registrationVehicleType])
  // Khi restore order tu API thi danh sach xe se duoc set lai trong useEffect ben duoi.
  // Truong hop con lai thi seed bang xe duoc truyen tu man truoc (neu co).
  const defaultVehicles = useMemo(
    () => (defaultVehicleFromState ? [defaultVehicleFromState] : []),
    [defaultVehicleFromState]
  )

  const [plateInput, setPlateInput] = useState('')
  const [plateError, setPlateError] = useState('')
  const [selectedVehicles, setSelectedVehicles] = useState(() => (shouldRestoreOrderDetail ? [] : defaultVehicles))
  const [popupConfig, setPopupConfig] = useState(null)
  const [hasSeededFromOrder, setHasSeededFromOrder] = useState(false)
  const [isPlateInputFocused, setIsPlateInputFocused] = useState(false)
  const [isVirtualKeyboardOpen, setIsVirtualKeyboardOpen] = useState(false)
  const blurTimeoutRef = useRef(null)
  const viewportHeightBeforeKeyboardRef = useRef(0)

  const selectedCount = selectedVehicles.length
  const canContinue = selectedCount > 0
  const selectedServices = useMemo(() => (trafficFineRegistrationProduct ? [trafficFineRegistrationProduct] : []), [trafficFineRegistrationProduct])

  // Mỗi đối tác có thể có luồng khác nhau.
  // Hiện tại Viettel Money sẽ thanh toán ngay ở màn chọn xe.
  const shouldPayAtVehicleStep = useMemo(() => shouldHandleTrafficFinePaymentAtVehicleStep(), [])

  const {
    isSubmitting,
    errorMessage,
    setErrorMessage,
    disabledPayBtn,
    handlePayment
  } = useTrafficFinePayment({
    vehicles: selectedVehicles,
    services: selectedServices,
    paymentMethodId: restoredPaymentMethodId,
    paymentMethodType: restoredPaymentMethodType,
    successPath: PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_SUCCESS
  })
  const shouldHideContinueButton = isVirtualKeyboardOpen
  const isContinueDisabled = shouldPayAtVehicleStep ? disabledPayBtn : !canContinue

  // Man nay chi su dung handlePayment cho luong VTM (shouldPayAtVehicleStep),
  // luong VTM khong hien QR ma redirect sang inapp nen khong can ModalPaymentQR
  // hay polling order o day.

  const registeredVehicleKeys = useMemo(() => {
    return new Set(
      listVehicleRegisteredPackage
        .map((item) => {
          const plate = normalizePlate(item?.vehicleIdentity || item?.vehicleIdentityNumber || item?.customerRecordPlatenumber || item?.plate || '')
          const vehicleType = Number(item?.vehicleType)

          if (!plate || !vehicleType) {
            return null
          }

          return `${plate}-${vehicleType}`
        })
        .filter(Boolean)
    )
  }, [listVehicleRegisteredPackage])

  const suggestedVehicles = useMemo(() => {
    if (!activeVehicleType) {
      return []
    }

    const uniqueVehicles = new Map()

    listVehicleNotRegistered.forEach((item) => {
      const plate = normalizePlate(item?.vehicleIdentity || item?.vehicleIdentityNumber || item?.customerRecordPlatenumber || item?.plate || '')
      const vehicleType = Number(item?.vehicleType)

      if (!plate || vehicleType !== activeVehicleType) {
        return
      }

      const key = `${plate}-${vehicleType}`

      if (!uniqueVehicles.has(key)) {
        uniqueVehicles.set(key, {
          plate,
          vehicleType
        })
      }
    })

    return [...uniqueVehicles.values()]
  }, [activeVehicleType, listVehicleNotRegistered])

  const closePopup = () => {
    setPopupConfig(null)
  }

  const openAlertPopup = (content, title = 'Thông báo') => {
    setPopupConfig({
      title,
      content,
      confirmText: 'Đã hiểu'
    })
  }

  const openConfirmPopup = ({ title, content, onConfirm, closeText = 'Hủy', confirmText = 'Tiếp tục' }) => {
    setPopupConfig({
      title,
      content,
      onConfirm,
      closeText,
      confirmText
    })
  }

  useEffect(() => {
    if (shouldRestoreOrderDetail && isLoadingOrderDetail) {
      return
    }

    if (!trafficFineRegistrationProduct) {
      history.replace(PATH.TRAFFIC_FINE_SERVICES)
    }
  }, [history, isLoadingOrderDetail, shouldRestoreOrderDetail, trafficFineRegistrationProduct])

  useEffect(() => {
    // Chi seed danh sach xe tu order detail dung 1 lan sau khi API tra ve.
    // Khong ghi de neu user da chinh sua danh sach (them/xoa) sau do.
    if (!shouldRestoreOrderDetail || isLoadingOrderDetail || orderVehicles.length === 0 || hasSeededFromOrder) {
      return
    }

    setSelectedVehicles(orderVehicles)
    setHasSeededFromOrder(true)
  }, [hasSeededFromOrder, isLoadingOrderDetail, orderVehicles, shouldRestoreOrderDetail])

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isPlateInputFocused) {
      viewportHeightBeforeKeyboardRef.current = 0
      setIsVirtualKeyboardOpen(false)
      return undefined
    }

    const updateVirtualKeyboardState = () => {
      const currentViewportHeight = getViewportHeight()

      if (!currentViewportHeight) {
        setIsVirtualKeyboardOpen(false)
        return
      }

      viewportHeightBeforeKeyboardRef.current = Math.max(
        viewportHeightBeforeKeyboardRef.current,
        currentViewportHeight
      )

      const keyboardHeight = viewportHeightBeforeKeyboardRef.current - currentViewportHeight
      setIsVirtualKeyboardOpen(keyboardHeight > VIRTUAL_KEYBOARD_HEIGHT_THRESHOLD)
    }

    const visualViewport = window.visualViewport

    updateVirtualKeyboardState()

    visualViewport?.addEventListener('resize', updateVirtualKeyboardState)
    visualViewport?.addEventListener('scroll', updateVirtualKeyboardState)
    window.addEventListener('resize', updateVirtualKeyboardState)

    return () => {
      visualViewport?.removeEventListener('resize', updateVirtualKeyboardState)
      visualViewport?.removeEventListener('scroll', updateVirtualKeyboardState)
      window.removeEventListener('resize', updateVirtualKeyboardState)
    }
  }, [isPlateInputFocused])

  const addVehicle = (plate) => {
    const safePlate = normalizePlate(plate)
    const registeredVehicleKey = `${safePlate}-${activeVehicleType}`

    if (!safePlate) {
      setPlateError('Vui lòng nhập biển số xe')
      return false
    }

    if (!activeVehicleType) {
      setPlateError('')
      openAlertPopup('Không xác định được loại xe. Vui lòng quay lại chọn gói dịch vụ.')
      return false
    }

    if (registeredVehicleKeys.has(registeredVehicleKey)) {
      setPlateError('')
      openAlertPopup('Biển số này đã nằm trong danh sách xe đã mua gói. Vui lòng chọn xe khác.')
      return false
    }

    if (selectedVehicles.some((vehicle) => vehicle.plate === safePlate && Number(vehicle.vehicleType) === Number(activeVehicleType))) {
      setPlateError('Biển số này đã có trong danh sách xe đăng ký gói.')
      return false
    }

    if (selectedVehicles.length >= maxVehicles) {
      setPlateError('')
      openAlertPopup('Thao tác không thành công do đã đạt giới hạn số lượng xe quy định.')
      return false
    }

    setSelectedVehicles((prevVehicles) => [
      ...prevVehicles,
      {
        plate: safePlate,
        vehicleType: activeVehicleType
      }
    ])
    setPlateError('')
    return true
  }

  const handleAddVehicleFromInput = () => {
    const safePlate = normalizePlate(plateInput)
    const validationError = validatePlateNumber(safePlate)

    if (validationError) {
      setPlateError(validationError)
      return
    }

    const isAdded = addVehicle(safePlate)

    if (isAdded) {
      setPlateInput('')
    }
  }

  const handleRemoveVehicle = (plate, vehicleType) => {
    setSelectedVehicles((prevVehicles) =>
      prevVehicles.filter((vehicle) => !(vehicle.plate === plate && Number(vehicle.vehicleType) === Number(vehicleType)))
    )
    setPlateError('')
  }

  const handleContinue = () => {
    // Với đối tác hỗ trợ thanh toán ngay, bấm tiếp theo sẽ gọi thanh toán trực tiếp.
    if (shouldPayAtVehicleStep) {
      void handlePayment()
      return
    }

    // Luồng thông thường vẫn chuyển sang màn thanh toán như cũ.
    // Chỉ đẩy vehicles và services mới nhất, không kéo theo state cũ trafficFineRegistrationVehicle/Product
    // vì màn thanh toán không đọc các key đó nữa.
    history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_PAYMENT, {
      vehicles: selectedVehicles,
      services: selectedServices
    })
  }

  if (!trafficFineRegistrationProduct) {
    return null
  }

  return (
    <LayoutPartner>
      <HeaderPartner title="Thông tin phương tiện" />

      <div className="TrafficFineNotificationVehicles">
        <div className="TrafficFineNotificationVehicles_inputRow">
          <BaseInput
            value={plateInput}
            onChange={(event) => {
              const value = sanitizePlateInput(event.target.value)
              setPlateInput(value)
              setPlateError('')
            }}
            showClear={Boolean(plateInput)}
            onClear={() => {
              setPlateInput('')
              setPlateError('')
            }}
            onFocus={() => {
              if (blurTimeoutRef.current) {
                window.clearTimeout(blurTimeoutRef.current)
                blurTimeoutRef.current = null
              }

              viewportHeightBeforeKeyboardRef.current = getViewportHeight()
              setIsPlateInputFocused(true)
            }}
            onBlur={() => {
              blurTimeoutRef.current = window.setTimeout(() => {
                setIsPlateInputFocused(false)
                setIsVirtualKeyboardOpen(false)
                viewportHeightBeforeKeyboardRef.current = 0
                blurTimeoutRef.current = null
              }, 180)
            }}
            placeholder="Ví dụ: 29A12345"
            error={plateError}
            buttonRight={
              <BaseButton
                type="right"
                className="TrafficFineNotificationVehicles_addButton"
                disabled={!plateInput}
                onClick={handleAddVehicleFromInput}>
                Thêm
              </BaseButton>
            }
          />
        </div>

        {suggestedVehicles.length > 0 ? (
          <div className="TrafficFineNotificationVehicles_suggestions">
            <DraggableScroll>
              <div className="TrafficFineNotificationVehicles_suggestionsTrack">
                {suggestedVehicles.map((vehicle) => {
                  const isSelected = selectedVehicles.some((selectedVehicle) => selectedVehicle.plate === vehicle.plate)

                  return (
                    <button
                      key={`${vehicle.plate}-${vehicle.vehicleType}`}
                      type="button"
                      className={`TrafficFineNotificationVehicles_suggestionItem ${
                        isSelected ? 'TrafficFineNotificationVehicles_suggestionItem_active' : ''
                      }`.trim()}
                      onClick={() => {
                        if (!isSelected) {
                          addVehicle(vehicle.plate)
                        }
                      }}>
                      <SafeImage src={activeVehicleTypeInfo?.iconUrl} className="TrafficFineNotificationVehicles_suggestionIcon" />
                      <span>{vehicle.plate}</span>
                    </button>
                  )
                })}
              </div>
            </DraggableScroll>
          </div>
        ) : null}

        <div className="TrafficFineNotificationVehicles_listHeader">{vehicleListTitle} ({selectedCount}/{maxVehicles})</div>

        <div className="TrafficFineNotificationVehicles_list">
          {selectedVehicles.map((vehicle) => {
            const vehicleTypeInfo = getVehicleTypeInfo(vehicle.vehicleType)

            return (
              <div key={`${vehicle.plate}-${vehicle.vehicleType}`} className="TrafficFineNotificationVehicles_vehicleItem">
                <div className="TrafficFineNotificationVehicles_vehicleInfo">
                  <SafeImage src={vehicleTypeInfo?.iconUrl} className="TrafficFineNotificationVehicles_vehicleIcon" />

                  <div className="TrafficFineNotificationVehicles_vehicleText">
                    <div className="TrafficFineNotificationVehicles_vehiclePlate">{vehicle.plate}</div>
                    <div className="TrafficFineNotificationVehicles_vehicleType">{vehicleTypeInfo?.label}</div>
                  </div>
                </div>

                <button
                  type="button"
                  className="TrafficFineNotificationVehicles_removeButton"
                  onClick={() => handleRemoveVehicle(vehicle.plate, vehicle.vehicleType)}
                  aria-label={`Xóa xe ${vehicle.plate}`}>
                  <RemoveIcon />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {!shouldHideContinueButton ? (
        <FixedBottom elementPaddingBottom="LayoutPartner">
          <BaseButton
            disabled={isContinueDisabled}
            onClick={() => {
              if (selectedCount < maxVehicles) {
                openConfirmPopup({
                  title: `Bạn chưa chọn đủ ${maxVehicles} xe`,
                  content: 'Sau khi thanh toán, bạn sẽ không thể thêm hoặc thay đổi xe. Bạn có muốn tiếp tục không?',
                  onConfirm: handleContinue
                })
                return
              }

              handleContinue()
            }}>
            Tiếp theo
          </BaseButton>
        </FixedBottom>
      ) : null}

      <BasePopup
        open={!!popupConfig}
        onClose={closePopup}
        onConfirm={() => {
          const nextAction = popupConfig?.onConfirm
          closePopup()
          nextAction?.()
        }}
        title={popupConfig?.title || 'Thông báo'}
        content={popupConfig?.content || ''}
        closeText={popupConfig?.closeText || ''}
        confirmText={popupConfig?.confirmText || ''}
      />

      <BasePopup
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title="Đã xảy ra lỗi"
        content="Không thể xử lý yêu cầu của bạn lúc này. Vui lòng thử lại sau."
        confirmText="Đã hiểu"
      />

      <LoadingScreen open={isSubmitting || (shouldRestoreOrderDetail && isLoadingOrderDetail)} />
    </LayoutPartner>
  )
}

